import React, { useEffect, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Container, Row, Col } from 'react-bootstrap';

import useToken from "../hooks/useToken";
import Content from './Content';
import Tags from './Tags';
import NavLinks from './NavLinks';
import RelatedPosts from './RelatedPosts';
import LeaveReply from './LeaveReply';
import LeftSidebar from './LeftSidebar';
import RightSidebar from './RightSidebar';
import MetaTags from './MetaTags';

import sectionGreenBg from '../assets/images/section-green.webp';
import wrapCenterBg from '../assets/images/wrap-center.webp';
import sectionCommentBg from '../assets/images/bg_pattern.svg';
import { API_ENDPOINTS } from '../utils/config';

const SingleBlog = ({ onLoadAttributes }) => {
    const { slug } = useParams();
    const { token } = useToken();

    const [post, setPost] = useState(null);
    const [error, setError] = useState(null);

    const fetchBlogPost = useCallback(async () => {
        const config = {
            headers: {
                'Content-Type': 'application/json; charset=UTF-8',
                Authorization: `Bearer ${token}`,
            },
            mode: 'no-cors',
        };

        try {
            const { data, status } = await axios.get(
                `${API_ENDPOINTS.POSTS_ENDPOINT}/${slug}`,
                config
            );

            if (status === 401) {
                setError('Unauthorized');
                return;
            }

            setPost(data.item);

            if (data.item.categories && onLoadAttributes) {
                onLoadAttributes({
                    categories: data.item.categories,
                    author_name: data.item.post_author_name,
                    author_image: data.item.post_author_url,
                    post_date: data.item.post_date
                });
            }

            // Log popular post
            await axios.post(
                `${API_ENDPOINTS.POPULAR_POST_ENDPOINT}/${data.item.id}`,
                {
                    wpp_id: data.item.id,
                    sampling: 0,
                    sampling_rate: 100,
                },
                config
            );
        } catch (err) {
            console.error( 'Error fetching post:', err );
            setError( 'Failed to load content' );
        }
    }, [slug, token, onLoadAttributes]);

    useEffect(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
        fetchBlogPost();
    }, [slug, fetchBlogPost]);

    if (error) {
        return (
            <Container className="my-5">
                <h3 className="text-danger">Error: {error}</h3>
            </Container>
        );
     }

    if ( !post ) {
        return (
            <Container className="my-5 text-center">
                <p>Loading content...</p>
            </Container>
        );
    }

    return (
        <>
            <MetaTags data={post} />

            {/* Main Blog Section */}
            <section
                id="blog-page"
                className="section"
                style={{ backgroundImage: `url(${wrapCenterBg})` }}
            >
                <div
                className="section-decoration top"
                style={{
                    backgroundImage: `url(${sectionGreenBg})`,
                    height: '35px',
                    backgroundColor: '#F8F9FA',
                }}
                />
                <Container>
                    <Row>
                        <Col lg={3} md={12} className="col-left-custom wow fadeInUp">
                            {post?.id && (
                                <LeftSidebar
                                    id={post.id}
                                    liked={post.liked_count}
                                    views={post.page_views}
                                    reading_time={post.reading_time}
                                    comment={Number(post.comment_count)}
                                    title={post.title}
                                />
                            )}
                        </Col>
                        <Col lg={6} md={12} className="col-middle-custom wow fadeInUp">
                            <Content data={post} />
                            <RelatedPosts data={post.id} />                         
                        </Col>
                        <Col lg={3} md={12} className="col-right-custom wow fadeInUp">
                            <RightSidebar />
                        </Col>
                    </Row>
                </Container>
            </section>
            {/* Comments Section */}
            <section
                id="comments"
                className="section"
                style={{ backgroundImage: `url(${sectionCommentBg})` }}
            >
                <Container>
                    
                    <Row>
                        <Col lg={2} md={12} className="wow fadeInUp"></Col>
                        <Col lg={8} md={12} className="wow fadeInUp">                          
                            <Tags data={post.tags} count={post.liked_count} />
                            <NavLinks data={post} />
                            <div className="position-relative d-flex text-center mb-5">
                                <h2 className="text-22 text-light opacity-4 text-uppercase fw-600 w-100 mb-0 font-primary wow fadeInUp">
                                    Discuss
                                </h2>
                                <p className="text-10 text-dark fw-600 position-absolute w-100 align-self-center font-secondary lh-base mb-0 wow fadeInUp">
                                    Comments
                                </p>
                            </div>
                            <LeaveReply id={post.id} />
                        </Col>
                        <Col lg={2} md={12} className="wow fadeInUp"></Col>
                    </Row>
                </Container>
            </section>
        </>
    );
};

export default SingleBlog;